import React, { useEffect, useState } from 'react';
import { useError } from '../../../error-provider/ErrorProvider';
import {
    Box,
    Flex,
    Text,
    useToast,
    Input,
    Divider,
    FormControl,
    FormHelperText,
} from '@chakra-ui/react';
import ZaraButton from 'components/custom-components/buttons/Button';
import useServerApi from '../../../hooks/useServerApi';

const TranscriberKeywords = ({ agentId }) => {
    const { showError } = useError();
    const toast = useToast();
    const [{ get, post }] = useServerApi();
    const [keywordString, setKeywordString] = useState('');
    const [loading, setLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(true);

    const fetchKeywords = async () => {
        if (!agentId) return;

        setIsFetching(true);
        try {
            const [success, response, error] = await get(
                `/rest_endpoints/agents/crud/transcriber-keywords/${agentId}`
            );
            if (success && response) {
                const fetchedKeywords = response.keywords || [];
                setKeywordString(fetchedKeywords.join(','));
            } else {
                showError('Failed to load transcriber keywords', error);
            }
        } catch (error) {
            console.error('Error fetching keywords:', error);
            showError(
                'Failed to load transcriber keywords',
                error instanceof Error ? error : new Error(String(error))
            );
        } finally {
            setIsFetching(false);
        }
    };

    useEffect(() => {
        if (agentId) {
            fetchKeywords();
        }
    }, [agentId]);

    const handleSave = async () => {
        const keywordsArray = keywordString.split(',').map(k => k.trim()).filter(k => k);

        setLoading(true);
        try {
            const [success, response, error] = await post(
                `/rest_endpoints/agents/crud/transcriber-keywords/${agentId}/update`,
                { keywords: keywordsArray }
            );
            if (success && response) {
                const responseKeywords = response.keywords || [];
                setKeywordString(responseKeywords.join(','));
                toast({
                    status: 'success',
                    title: 'Keywords updated successfully',
                    isClosable: true,
                    duration: 3000,
                });
            } else {
                toast({
                    status: 'error',
                    title: 'Failed to update keywords',
                    description: (error && error.detail) || 'Could not save keywords',
                    isClosable: true,
                    duration: 3000,
                });
                showError('Failed to update keywords', error);
            }
        } catch (error) {
            toast({
                status: 'error',
                title: 'Failed to update keywords',
                description: error instanceof Error ? error.message : String(error),
                isClosable: true,
                duration: 3000,
            });
            showError(
                'Failed to update keywords',
                error instanceof Error
                    ? error
                    : new Error(String(error))
            );
        } finally {
            setLoading(false);
        }
    };

    if (isFetching) {
        return (
            <Box>
                <Text fontSize='md' color='gray.500'>
                    Loading keywords...
                </Text>
            </Box>
        );
    }

    return (
        <Box
            borderWidth='1px'
            borderRadius='lg'
            borderColor='gray.200'
            p='6'
            bg='white'
        >
            <Flex
                fontSize={{ base: '20px', sm: '24px' }}
                lineHeight='28px'
                fontWeight='600'
                textAlign='left'
                gap='3'
                alignItems='center'
                mb='2'
            >
                <Text>Transcriber Keywords</Text>
            </Flex>

            <Text fontSize='sm' color='gray.600' mb='6'>
                Add keywords that the speech-to-text engine should
                recognize for improved accuracy. Enter keywords separated by commas.
            </Text>

            <Divider mb='6' />

            <FormControl>
                <Box mb='6'>
                    <Input
                        placeholder="e.g., dialzara, duetshe, something special"
                        value={keywordString}
                        onChange={(e) => setKeywordString(e.target.value)}
                        size='md'
                        isDisabled={loading}
                        borderColor='gray.300'
                        _focus={{
                            borderColor: 'blue.500',
                            boxShadow: '0 0 0 1px blue.500',
                        }}
                        bg='gray.50'
                    />
                </Box>

                <FormHelperText color='gray.600' mb='6'>
                    {(() => {
                        const keywordsArray = keywordString.split(',').map(k => k.trim()).filter(k => k);
                        const hasKeywords = keywordsArray.length > 0;
                        return hasKeywords
                            ? `${keywordsArray.length} keyword${keywordsArray.length !== 1 ? 's' : ''} configured`
                            : 'No keywords configured yet';
                    })()}
                </FormHelperText>
            </FormControl>

            <Flex gap='3' alignItems='center'>
                <ZaraButton
                    onClick={handleSave}
                    isLoading={loading}
                    isDisabled={loading || keywordString.split(',').map(k => k.trim()).filter(k => k).length === 0}
                    variant='primary'
                    fontSize='md'
                    w='auto'
                    type='submit'
                >
                    Save
                </ZaraButton>
            </Flex>
        </Box>
    );
};

export default TranscriberKeywords;
